import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useToast } from '../context/ToastContext';
import {
    AlertCircle, CheckCircle2, Clock, Filter, MessageSquare,
    Reply, Search, Send, Store, Ticket, X, ChevronDown, ChevronUp, BookOpen
} from 'lucide-react';
import ConfirmModal from '../components/ConfirmModal';

const AdminSupportPage = () => {
    const { showToast } = useToast();
    const [loading, setLoading] = useState(true);
    const [tickets, setTickets] = useState<any[]>([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterStatus, setFilterStatus] = useState('all');

    // Reply Modal state
    const [selectedTicket, setSelectedTicket] = useState<any>(null);
    const [replyText, setReplyText] = useState('');
    const [replyLoading, setReplyLoading] = useState(false);
    const [closeModal, setCloseModal] = useState<{ isOpen: boolean; ticketId: string | null; loading: boolean }>({
        isOpen: false,
        ticketId: null,
        loading: false
    });

    useEffect(() => {
        fetchTickets();
    }, []);

    const fetchTickets = async () => {
        setLoading(true);
        try {
            const res = await axios.get('https://zapshop.app/api/admin/support/tickets');
            setTickets(res.data);
        } catch (err) {
            console.error('Erro ao buscar tickets:', err);
            showToast('Erro ao carregar chamados.', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleReply = async () => {
        if (!replyText || !selectedTicket) return;

        setReplyLoading(true);
        try {
            await axios.put(`https://zapshop.app/api/admin/support/tickets/${selectedTicket.id}/reply`, {
                reply: replyText,
                status: 'REPLIED'
            });
            showToast('Resposta enviada com sucesso!', 'success');
            setSelectedTicket(null);
            setReplyText('');
            fetchTickets();
        } catch (err) {
            showToast('Erro ao enviar resposta.', 'error');
        } finally {
            setReplyLoading(false);
        }
    };

    const updateTicketStatus = async (id: string, status: string) => {
        if (status === 'CLOSED') {
            setCloseModal({ isOpen: true, ticketId: id, loading: false });
            return;
        }

        try {
            await axios.put(`https://zapshop.app/api/admin/support/tickets/${id}/reply`, {
                status
            });
            showToast('Status atualizado com sucesso!', 'success');
            fetchTickets();
        } catch (err) {
            showToast('Erro ao atualizar status.', 'error');
        }
    };

    const confirmCloseTicket = async () => {
        if (!closeModal.ticketId) return;
        setCloseModal(prev => ({ ...prev, loading: true }));
        try {
            await axios.put(`https://zapshop.app/api/admin/support/tickets/${closeModal.ticketId}/reply`, {
                status: 'CLOSED'
            });
            showToast('Chamado finalizado.', 'success');
            setCloseModal({ isOpen: false, ticketId: null, loading: false });
            fetchTickets();
        } catch (err) {
            showToast('Erro ao finalizar chamado.', 'error');
            setCloseModal(prev => ({ ...prev, loading: false }));
        }
    };

    const filteredTickets = tickets.filter(t => {
        const matchesSearch = t.tenant_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            t.description?.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesStatus = filterStatus === 'all' || t.status === filterStatus;
        return matchesSearch && matchesStatus;
    });

    const getStatusStyle = (status: string) => {
        switch (status) {
            case 'OPEN': return 'bg-amber-100 text-amber-700 border-amber-200';
            case 'REPLIED': return 'bg-blue-100 text-blue-700 border-blue-200';
            case 'CLOSED': return 'bg-gray-100 text-gray-600 border-gray-200';
            default: return 'bg-gray-50 text-gray-500';
        }
    };

    return (
        <div className="space-y-6 animate-fade-in">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <div>
                    <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
                        <Ticket className="text-emerald-600" size={28} /> Central de Suporte
                    </h2>
                    <p className="text-gray-500 text-sm">Gerencie solicitações e falhas reportadas pelos lojistas.</p>
                </div>

                <div className="flex items-center gap-2 w-full md:w-auto">
                    <div className="relative flex-1 md:w-64">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                        <input
                            type="text"
                            placeholder="Buscar loja ou problema..."
                            className="input-primary w-full pl-10"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                    <select
                        className="input-primary w-40"
                        value={filterStatus}
                        onChange={(e) => setFilterStatus(e.target.value)}
                    >
                        <option value="all">Todos Status</option>
                        <option value="OPEN">Abertos</option>
                        <option value="REPLIED">Respondidos</option>
                        <option value="CLOSED">Finalizados</option>
                    </select>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
                {/* COLUNA: CHAMADOS */}
                <div className="space-y-6">
                    <h3 className="text-sm font-black text-gray-400 uppercase tracking-[0.2em] px-1">Últimos Chamados</h3>
                    {loading ? (
                        <div className="text-center py-20 bg-white rounded-2xl border border-gray-100">
                            <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-emerald-500 border-t-transparent mb-4"></div>
                            <p className="text-gray-500">Carregando chamados...</p>
                        </div>
                    ) : filteredTickets.length === 0 ? (
                        <div className="text-center py-20 bg-white rounded-2xl border border-dashed border-gray-200">
                            <MessageSquare size={48} className="mx-auto text-gray-200 mb-4" />
                            <p className="text-gray-500 font-medium">Nenhum chamado encontrado.</p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                            {filteredTickets.map((ticket) => (
                                <div key={ticket.id} className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden flex flex-col hover:shadow-md transition-all">
                                    <div className="p-5 flex-1">
                                        <div className="flex justify-between items-start mb-4">
                                            <div className={`px-2 py-0.5 rounded-full text-[10px] font-bold border ${getStatusStyle(ticket.status)}`}>
                                                {ticket.status === 'OPEN' ? 'AGUARDANDO' : ticket.status}
                                            </div>
                                            <span className="text-[10px] font-bold text-gray-400 uppercase tracking-tighter">#{ticket.id.slice(0, 8)}</span>
                                        </div>

                                        <div className="flex items-center gap-2 mb-3">
                                            <div className="w-8 h-8 rounded-full bg-emerald-50 flex items-center justify-center">
                                                <Store size={16} className="text-emerald-600" />
                                            </div>
                                            <div className="flex-1 text-left">
                                                <h4 className="text-sm font-bold text-gray-900 truncate">{ticket.tenant_name}</h4>
                                                <div className="flex items-center gap-1.5 mt-0.5">
                                                    {ticket.ticket_type === 'FALHA' ? (
                                                        <AlertCircle size={12} className="text-red-500" />
                                                    ) : (
                                                        <CheckCircle2 size={12} className="text-blue-500" />
                                                    )}
                                                    <span className="text-[10px] uppercase font-bold text-gray-500">{ticket.ticket_type}</span>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="p-3 bg-gray-50 rounded-xl mb-4 text-left">
                                            <p className="text-xs text-gray-700 leading-relaxed italic">"{ticket.description}"</p>
                                        </div>

                                        {ticket.image_url && (
                                            <div className="mb-4">
                                                <a
                                                    href={`https://zapshop.app${ticket.image_url}`}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    className="block relative group rounded-xl overflow-hidden aspect-video border border-gray-100 bg-gray-100"
                                                >
                                                    <img src={`https://zapshop.app${ticket.image_url}`} alt="Evidência" className="w-full h-full object-cover" />
                                                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                                        <Search className="text-white" size={24} />
                                                    </div>
                                                </a>
                                            </div>
                                        )}

                                        {ticket.admin_reply && (
                                            <div className="p-3 border-l-2 border-emerald-400 bg-emerald-50/30 rounded-r-xl mb-4 text-left">
                                                <p className="text-[10px] font-bold text-emerald-700 uppercase mb-1">Minha Resposta:</p>
                                                <p className="text-xs text-gray-700">{ticket.admin_reply}</p>
                                            </div>
                                        )}
                                    </div>

                                    <div className="p-4 bg-gray-50 border-t border-gray-100 flex items-center gap-2">
                                        <button
                                            onClick={() => setSelectedTicket(ticket)}
                                            className="flex-1 btn-primary py-2 text-xs flex items-center justify-center gap-2"
                                        >
                                            <Reply size={14} /> Responder
                                        </button>
                                        <select
                                            className="bg-white border border-gray-200 rounded-lg px-2 py-2 text-[10px] font-bold uppercase text-gray-500 focus:ring-emerald-500"
                                            value={ticket.status}
                                            onChange={(e) => updateTicketStatus(ticket.id, e.target.value)}
                                        >
                                            <option value="OPEN">Aberto</option>
                                            <option value="REPLIED">Respondido</option>
                                            <option value="CLOSED">Fechado</option>
                                        </select>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* COLUNA: FAQ */}
                <div className="space-y-6">
                    <h3 className="text-sm font-black text-gray-400 uppercase tracking-[0.2em] px-1">Perguntas Frequentes (FAQ)</h3>
                    <div className="bg-white rounded-3xl shadow-sm border border-gray-100 overflow-hidden divide-y divide-gray-100">
                        <FAQItem
                            question="Como conecto meu WhatsApp?"
                            answer="Vá na aba 'WhatsApp', clique em 'Conectar Instância' e escaneie o QR Code com seu aparelho. Certifique-se de que o WhatsApp Web não esteja aberto em outro lugar (navegador ou outro computador) para evitar desconexões."
                            icon={<Store className="text-emerald-500" size={18} />}
                        />
                        <FAQItem
                            question="Como a IA do ZapShop funciona?"
                            answer="Nossa IA utiliza o modelo GPT-4o-mini para atender seus clientes de forma humanizada. Você pode configurar a 'Personalidade' da IA na aba de configurações para que ela fale exatamente como você deseja, use gírias ou seja mais formal."
                            icon={<AlertCircle className="text-blue-500" size={18} />}
                        />
                        <FAQItem
                            question="Como configuro meus pagamentos?"
                            answer="Utilizamos o Mercado Pago para processar pagamentos de forma segura. Basta inserir seu 'Access Token' de produção nas configurações da loja. Seus clientes poderão pagar via PIX ou Cartão e o dinheiro cai direto na sua conta do MP."
                            icon={<Clock className="text-amber-500" size={18} />}
                        />
                        <FAQItem
                            question="Posso usar meu próprio domínio (ex: .com.br)?"
                            answer="Sim! Você pode configurar um domínio próprio apontando o DNS (Tipo A) para o IP 31.97.242.168. Após configurar o DNS, insira o domínio na aba 'Configurações' e o SSL será gerado automaticamente em até 24h."
                            icon={<CheckCircle2 className="text-purple-500" size={18} />}
                        />
                        <FAQItem
                            question="Qual o limite de produtos?"
                            answer="O limite depende do seu plano: Básico (100), Intermediário (300) ou PRO (Ilimitado). Se você atingir o limite, seus clientes ainda verão a vitrine, mas você não poderá cadastrar novos itens sem fazer o upgrade."
                            icon={<Ticket className="text-rose-500" size={18} />}
                        />
                        <FAQItem
                            question="Como gerenciar meus pedidos?"
                            answer="Sempre que um cliente finaliza uma compra, você recebe uma notificação no painel 'Pedidos'. Lá você pode ver os detalhes do cliente, produtos comprados e atualizar o status para 'Despachado' ou 'Entregue' para informar o cliente."
                            icon={<Search className="text-indigo-500" size={18} />}
                        />
                    </div>

                    {/* Banner de Suporte no FAQ */}
                    <div className="bg-emerald-900 rounded-3xl p-8 text-white relative overflow-hidden group">
                        <div className="absolute top-0 right-0 p-8 transform translate-x-4 -translate-y-4 opacity-10 group-hover:scale-110 transition-transform">
                            <BookOpen size={120} />
                        </div>
                        <div className="relative z-10">
                            <h4 className="text-xl font-black mb-2">Ainda precisa de ajuda?</h4>
                            <p className="text-emerald-100/80 text-sm mb-6 max-w-xs">Se sua dúvida não foi respondida acima, nossa equipe está pronta para te ajudar via WhatsApp.</p>
                            <a
                                href="https://wa.me/5521991000120?text=Preciso%20de%20ajuda%20com%20minha%20loja%20ZapShop"
                                target="_blank"
                                rel="noreferrer"
                                className="inline-flex items-center gap-2 bg-white text-emerald-900 font-black py-3 px-6 rounded-2xl hover:bg-emerald-50 transition-all active:scale-95 shadow-xl shadow-black/20"
                            >
                                <MessageSquare size={18} /> Chamar no WhatsApp
                            </a>
                        </div>
                    </div>
                </div>
            </div>

            {/* Reply Modal */}
            {selectedTicket && (
                <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4 backdrop-blur-sm animate-fade-in">
                    <div className="bg-white rounded-3xl w-full max-w-lg shadow-2xl overflow-hidden animate-scale-in">
                        <div className="p-6 bg-emerald-600 text-white flex justify-between items-center">
                            <div>
                                <h3 className="font-bold text-lg">Responder Chamado</h3>
                                <p className="text-emerald-100 text-xs">Loja: {selectedTicket.tenant_name}</p>
                            </div>
                            <button onClick={() => setSelectedTicket(null)} className="p-2 hover:bg-white/10 rounded-full transition-colors">
                                <X size={20} />
                            </button>
                        </div>

                        <div className="p-6 space-y-4">
                            <div className="p-4 bg-gray-50 rounded-2xl border border-gray-100">
                                <p className="text-xs font-bold text-gray-400 uppercase mb-2">Problema Reportado:</p>
                                <p className="text-sm text-gray-700 italic">"{selectedTicket.description}"</p>
                            </div>

                            <div>
                                <label className="text-xs font-bold text-gray-500 uppercase block mb-2">Sua Resposta</label>
                                <textarea
                                    className="input-primary w-full h-32 py-3"
                                    placeholder="Escreva como o lojista deve proceder ou a solução aplicada..."
                                    value={replyText}
                                    onChange={(e) => setReplyText(e.target.value)}
                                />
                            </div>

                            <button
                                onClick={handleReply}
                                disabled={replyLoading || !replyText}
                                className="w-full btn-primary py-4 text-sm font-bold flex items-center justify-center gap-2 shadow-xl shadow-emerald-600/20 active:scale-95 transition-all"
                            >
                                {replyLoading ? 'Enviando...' : (
                                    <>
                                        <Send size={18} />
                                        Enviar Resposta ao Lojista
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <ConfirmModal
                isOpen={closeModal.isOpen}
                onClose={() => setCloseModal({ isOpen: false, ticketId: null, loading: false })}
                onConfirm={confirmCloseTicket}
                loading={closeModal.loading}
                title="Finalizar Chamado?"
                message="Deseja realmente finalizar este chamado? Isso indicará que a solicitação foi atendida."
                confirmText="Sim, Finalizar"
                cancelText="Voltar"
                type="warning"
            />
        </div>
    );
};

const FAQItem = ({ question, answer, icon }: any) => {
    const [isOpen, setIsOpen] = useState(false);
    return (
        <div className="transition-all">
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="w-full text-left p-6 flex items-center justify-between hover:bg-gray-50/80 transition-all font-bold text-gray-900 gap-4"
            >
                <div className="flex items-center gap-4">
                    <div className="hidden sm:block">
                        {icon}
                    </div>
                    <span className="text-sm">{question}</span>
                </div>
                {isOpen ? <ChevronUp size={18} className="text-emerald-500" /> : <ChevronDown size={18} className="text-gray-300" />}
            </button>
            <div className={`overflow-hidden transition-all duration-300 ${isOpen ? 'max-h-96 pb-6 px-6' : 'max-h-0'}`}>
                <div className="p-4 bg-emerald-50/50 rounded-2xl border border-emerald-50/50 text-xs text-emerald-800 leading-relaxed">
                    {answer}
                </div>
            </div>
        </div>
    );
};

export default AdminSupportPage;
